import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { employeeWalletAPI } from '../../lib/walletApi';
import { ArrowUpRight, ArrowDownLeft, AlertCircle, X, Download, Share2, CheckCircle, XCircle, Clock, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function EmployeeTransactions() {
  const [txns, setTxns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const receiptRef = useRef(null);

  const loadTxns = () => employeeWalletAPI.getTransactions().then(setTxns).catch(() => {});

  useEffect(() => { loadTxns(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTxns();
    setRefreshing(false);
  };

  const statusConfig = {
    completed: { label: 'Successful', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle },
    failed: { label: 'Failed', color: 'text-red-500', bg: 'bg-red-50', icon: XCircle },
    pending: { label: 'Pending', color: 'text-amber-500', bg: 'bg-amber-50', icon: Clock },
    processing: { label: 'Processing', color: 'text-blue-500', bg: 'bg-blue-50', icon: Clock },
  };

  const getStatusInfo = (status) => statusConfig[status] || statusConfig.pending;

  const handleShare = async (t) => {
    const text = `Pebbles Transaction Receipt\n${t.direction === 'credit' ? 'Received' : 'Sent'}: NGN ${Number(t.amount).toLocaleString()}\nTo: ${t.payout_account_name || t.description}\nRef: ${t.reference}\nDate: ${new Date(t.created_at).toLocaleString()}\nStatus: ${getStatusInfo(t.status).label}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Pebbles Receipt', text }); } catch {}
    } else {
      await navigator.clipboard.writeText(text);
      toast.success('Receipt copied to clipboard');
    }
  };

  const handleDownload = (t) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const w = 600, h = 520;
    canvas.width = w; canvas.height = h;

    // Background
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, w, 6);

    // Header
    ctx.fillStyle = '#0f172a'; ctx.font = 'bold 22px system-ui'; ctx.fillText('Pebbles', 32, 48);
    ctx.fillStyle = '#64748b'; ctx.font = '13px system-ui'; ctx.fillText('Transaction Receipt', 32, 68);

    // Amount
    const isCredit = t.direction === 'credit';
    ctx.fillStyle = isCredit ? '#059669' : '#0f172a';
    ctx.font = 'bold 36px system-ui';
    ctx.fillText(`${isCredit ? '+' : '-'}NGN ${Number(t.amount).toLocaleString()}`, 32, 128);

    // Fee
    if (Number(t.fee) > 0) {
      ctx.fillStyle = '#94a3b8'; ctx.font = '13px system-ui';
      ctx.fillText(`Fee: NGN ${Number(t.fee).toLocaleString()}`, 32, 152);
    }

    // Status badge
    const si = getStatusInfo(t.status);
    ctx.fillStyle = t.status === 'completed' ? '#d1fae5' : t.status === 'failed' ? '#fee2e2' : '#fef3c7';
    const badgeX = 420, badgeY = 108;
    ctx.beginPath(); ctx.roundRect(badgeX, badgeY, 140, 28, 14); ctx.fill();
    ctx.fillStyle = t.status === 'completed' ? '#059669' : t.status === 'failed' ? '#dc2626' : '#d97706';
    ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'center'; ctx.fillText(si.label, badgeX + 70, badgeY + 18); ctx.textAlign = 'left';

    // Divider
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(32, 180); ctx.lineTo(w - 32, 180); ctx.stroke();

    // Details
    const details = [
      ['Recipient', t.payout_account_name || t.description || '-'],
      ['Bank', t.payout_bank_name || (isCredit ? 'Pebbles Wallet' : '-')],
      ['Account', t.payout_account_number || '-'],
      ['Reference', t.reference],
      ['Date', new Date(t.created_at).toLocaleString()],
      ['Narration', t.narration || t.description || '-'],
    ];
    let y = 210;
    details.forEach(([label, value]) => {
      ctx.fillStyle = '#94a3b8'; ctx.font = '12px system-ui'; ctx.fillText(label, 32, y);
      ctx.fillStyle = '#1e293b'; ctx.font = '14px system-ui';
      const v = String(value).length > 45 ? String(value).slice(0, 45) + '...' : String(value);
      ctx.fillText(v, 200, y);
      y += 36;
    });

    // Footer
    ctx.strokeStyle = '#e2e8f0'; ctx.beginPath(); ctx.moveTo(32, y + 10); ctx.lineTo(w - 32, y + 10); ctx.stroke();
    ctx.fillStyle = '#94a3b8'; ctx.font = '11px system-ui';
    ctx.fillText('pebbles.financial', 32, y + 36);
    ctx.fillText('Powered by Pebbles', w - 160, y + 36);

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `pebbles-receipt-${t.reference}.png`; a.click();
      URL.revokeObjectURL(url);
    });
  };

  const copyRef = (ref) => { navigator.clipboard.writeText(ref); toast.success('Reference copied'); };

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Transactions</CardTitle>
            <button onClick={handleRefresh} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="refresh-txns-btn">
              <RefreshCw className={`w-4 h-4 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {txns.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">No transactions yet</p>}
          {txns.map((t, i) => {
            const isCredit = t.direction === 'credit';
            const isFailed = t.status === 'failed';
            const si = getStatusInfo(t.status);
            const StatusIcon = si.icon;
            return (
              <div key={i}
                className={`flex items-center justify-between py-3 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors ${isFailed ? 'opacity-70' : ''}`}
                onClick={() => setSelected(t)}
                data-testid={`txn-row-${i}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isFailed ? 'bg-gray-100' : isCredit ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    {isFailed ? <AlertCircle className="w-4 h-4 text-gray-400" /> : isCredit ? <ArrowDownLeft className="w-4 h-4 text-emerald-600" /> : <ArrowUpRight className="w-4 h-4 text-red-600" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${isFailed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{t.description || t.type}</p>
                      {isFailed && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-500 font-medium">Failed</span>}
                      {t.status === 'pending' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-500 font-medium">Pending</span>}
                    </div>
                    <p className="text-[10px] text-gray-400">{new Date(t.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <span className={`text-sm font-semibold ${isFailed ? 'text-gray-400' : isCredit ? 'text-emerald-600' : 'text-red-600'}`}>
                  {isCredit ? '+' : '-'}NGN {Number(t.amount).toLocaleString()}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Receipt Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => setSelected(null)} data-testid="receipt-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
            {/* Header bar */}
            <div className="h-1.5 bg-slate-900" />
            <div className="p-5" ref={receiptRef}>
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Pebbles</h3>
                  <p className="text-xs text-slate-400">Transaction Receipt</p>
                </div>
                <button onClick={() => setSelected(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-400" /></button>
              </div>

              {/* Amount */}
              <div className="text-center py-4">
                <p className={`text-3xl font-bold ${selected.direction === 'credit' ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {selected.direction === 'credit' ? '+' : '-'}NGN {Number(selected.amount).toLocaleString()}
                </p>
                {Number(selected.fee) > 0 && <p className="text-xs text-slate-400 mt-1">Fee: NGN {Number(selected.fee).toLocaleString()}</p>}
              </div>

              {/* Status */}
              {(() => {
                const si = getStatusInfo(selected.status);
                const Icon = si.icon;
                return (
                  <div className={`flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-full ${si.bg} mx-auto w-fit mb-5`}>
                    <Icon className={`w-3.5 h-3.5 ${si.color}`} />
                    <span className={`text-xs font-semibold ${si.color}`}>{si.label}</span>
                  </div>
                );
              })()}

              {selected.status === 'failed' && selected.failure_reason && (
                <p className="text-xs text-red-500 text-center mb-4 px-4">{selected.failure_reason}</p>
              )}

              {/* Details */}
              <div className="border-t border-slate-100 pt-4 space-y-3">
                {[
                  ['Recipient', selected.payout_account_name || selected.description],
                  selected.payout_bank_name && ['Bank', selected.payout_bank_name],
                  selected.payout_account_number && ['Account', selected.payout_account_number],
                  ['Reference', (
                    <span className="flex items-center gap-1 cursor-pointer" onClick={() => copyRef(selected.reference)}>
                      <span className="font-mono text-[11px]">{selected.reference}</span>
                      <Copy className="w-3 h-3 text-slate-400" />
                    </span>
                  )],
                  ['Date', new Date(selected.created_at).toLocaleString()],
                  selected.narration && ['Narration', selected.narration],
                ].filter(Boolean).map(([label, value], idx) => (
                  <div key={idx} className="flex justify-between items-start">
                    <span className="text-xs text-slate-400 shrink-0">{label}</span>
                    <span className="text-xs text-slate-700 font-medium text-right ml-4 break-all">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-slate-100 p-4 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => handleDownload(selected)} data-testid="receipt-download-btn">
                <Download className="w-3.5 h-3.5" /> Download
              </Button>
              <Button size="sm" className="flex-1 gap-1.5 bg-slate-900 hover:bg-slate-800" onClick={() => handleShare(selected)} data-testid="receipt-share-btn">
                <Share2 className="w-3.5 h-3.5" /> Share
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
